// src/core/hooks/useScreenResolution.ts
import { useState, useEffect } from 'react';

export type ScreenCategory = 'HD' | '2K' | '4K';

export interface ScreenResolution {
  width: number;
  height: number;
  category: ScreenCategory;
  textScale: number;
  isLargeScreen: boolean;
  dpr: number;
}

/**
 * Hook para detectar la resolución de pantalla y calcular factores de escala
 * Útil para ajustar tamaños de texto según la resolución del dispositivo
 */
export function useScreenResolution(): ScreenResolution {
  const [resolution, setResolution] = useState<ScreenResolution>(() => {
    if (typeof window === 'undefined') {
      return {
        width: 1920,
        height: 1080,
        category: 'HD',
        textScale: 1.2,
        isLargeScreen: false,
        dpr: 1,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = window.devicePixelRatio || 1;

    // Calcular tamaño físico real de la pantalla (considerando DPR)
    const screenWidth = window.screen.width || width;
    const screenHeight = window.screen.height || height;

    const physicalWidth = Math.max(screenWidth * dpr, width * dpr);
    const physicalHeight = Math.max(screenHeight * dpr, height * dpr);

    let category: ScreenCategory = 'HD';
    let textScale = 1.0; // Base scale
    let isLargeScreen = false;

    if (physicalWidth >= 2560 || physicalHeight >= 1440) {
      category = '4K';
      textScale = 2.3;
      isLargeScreen = true;
    } else if (physicalWidth >= 1920 || physicalHeight >= 1080) {
      category = '2K';
      textScale = 1.5;
      isLargeScreen = true;
    } else if (physicalWidth >= 1366) {
      category = 'HD';
      textScale = 1.2;
      isLargeScreen = true;
    } else {
      category = 'HD';
      textScale = 1.0;
      isLargeScreen = false;
    }

    return {
      width,
      height,
      category,
      textScale,
      isLargeScreen,
      dpr,
    };
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateResolution = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;

      const screenWidth = window.screen.width || width;
      const screenHeight = window.screen.height || height;

      const physicalWidth = Math.max(screenWidth * dpr, width * dpr);
      const physicalHeight = Math.max(screenHeight * dpr, height * dpr);

      let category: ScreenCategory = 'HD';
      let textScale = 1.0;
      let isLargeScreen = false;

      if (physicalWidth >= 2560 || physicalHeight >= 1440) {
        category = '4K';
        textScale = 2.3;
        isLargeScreen = true;
      } else if (physicalWidth >= 1920 || physicalHeight >= 1080) {
        category = '2K';
        textScale = 1.5;
        isLargeScreen = true;
      } else if (physicalWidth >= 1366) {
        category = 'HD';
        textScale = 1.2;
        isLargeScreen = true;
      } else {
        category = 'HD';
        textScale = 1.0;
        isLargeScreen = false;
      }

      // Compensar por el zoom del navegador (DPR)
      // Si el navegador ya está escalando todo, nosotros debemos reducir nuestro factor
      // para que el tamaño físico final sea el deseado.
      const adjustedScale = textScale / dpr;

      // Aplicar escala a CSS variable
      document.documentElement.style.setProperty('--font-scale', adjustedScale.toString());

      // También guardar en el estado
      setResolution({
        width,
        height,
        category,
        textScale,
        isLargeScreen,
        dpr,
      });

      console.log('[useScreenResolution] Actualizado:', {
        physicalWidth,
        dpr,
        category,
        textScale,
        adjustedScale,
        isLargeScreen
      });
    };

    // Debounce para evitar cálculos excesivos
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateResolution, 150);
    };

    window.addEventListener('resize', handleResize);
    updateResolution(); // Llamar una vez al montar

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return resolution;
}


